import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { SuggestedGuests } from "../components/SuggestedGuests.js";
import { api } from "../lib/api.js";

function EpisodeDetailPageWithErrorBoundary() {
  return (
    <ErrorBoundary componentName="EpisodeDetailPage">
      <EpisodeDetailPage />
    </ErrorBoundary>
  );
}

export const Route = createFileRoute("/episodes/$episodeId")({ component: EpisodeDetailPageWithErrorBoundary });

interface Episode {
  id: string;
  title: string;
  number?: string;
  description?: string;
  status: string;
  scheduledAt?: string;
  recordedAt?: string;
  publishedAt?: string;
  recordingUrl?: string;
  episodeUrl?: string;
  topics: string[];
  prepNotes?: string;
  guest: { id: string; name: string; email: string };
}

const statusLabels: Record<string, string> = { inquiry: "Inquiry", scheduled: "Scheduled", recorded: "Recorded", edited: "Edited", published: "Published", promoted: "Promoted", completed: "Completed" };
const statusColors: Record<string, string> = { inquiry: "bg-yellow-100 text-yellow-800", scheduled: "bg-blue-100 text-blue-800", recorded: "bg-purple-100 text-purple-800", edited: "bg-pink-100 text-pink-800", published: "bg-green-100 text-green-800", promoted: "bg-teal-100 text-teal-800", completed: "bg-gray-100 text-gray-800" };

function EpisodeDetailPage() {
  const { user } = useAuth();
  const { episodeId } = Route.useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Episode>>({});

  useEffect(() => {
    if (!user) return;
    api.get(`/api/episodes/${episodeId}`).then(r => r.json()).then(d => { setEpisode(d.episode); setEditForm(d.episode); setLoading(false); });
  }, [episodeId, user]);

  const handleSave = async () => {
    try {
      const res = await api.patch(`/api/episodes/${episodeId}`, {
        title: editForm.title, status: editForm.status, number: editForm.number, description: editForm.description,
        scheduledAt: editForm.scheduledAt, recordedAt: editForm.recordedAt, publishedAt: editForm.publishedAt,
        recordingUrl: editForm.recordingUrl, episodeUrl: editForm.episodeUrl,
        topics: Array.isArray(editForm.topics) ? editForm.topics : (editForm.topics as unknown as string).split(",").map(t => t.trim()).filter(Boolean),
        prepNotes: editForm.prepNotes
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setEpisode(data.episode);
      setIsEditing(false);
    } catch { alert("Failed to save"); }
  };

  if (!user) return <div>Loading...</div>;
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!episode) return <div>Episode not found</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/episodes/" className="text-sm text-muted-foreground hover:text-foreground">← Back to episodes</Link>
          <h1 className="text-3xl font-bold mt-2">{episode.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[episode.status]}`}>{statusLabels[episode.status]}</span>
            <Link to="/guests/$guestId" params={{ guestId: episode.guest.id }} className="text-muted-foreground hover:text-primary">with {episode.guest.name}</Link>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button onClick={handleSave} className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium">Save</button>
              <button onClick={() => { setEditForm(episode); setIsEditing(false); }} className="px-4 py-2 border rounded-md font-medium">Cancel</button>
            </>
          ) : (
            <button onClick={() => setIsEditing(true)} className="px-4 py-2 border rounded-md font-medium hover:bg-muted">Edit</button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="border rounded-lg p-6">
            <h2 className="font-semibold text-lg mb-4">Details</h2>
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Title</label><input type="text" value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
                  <div><label className="block text-sm font-medium mb-1">Number</label><input type="text" value={editForm.number || ""} onChange={e => setEditForm({ ...editForm, number: e.target.value })} className="w-full px-3 py-2 border rounded-md" /></div>
                </div>
                <div><label className="block text-sm font-medium mb-1">Status</label><select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full px-3 py-2 border rounded-md">{Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">Description</label><textarea value={editForm.description || ""} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows={3} /></div>
              </div>
            ) : (
              <div className="space-y-3">
                {episode.number && <div><span className="text-sm text-muted-foreground">Episode Number</span><p>#{episode.number}</p></div>}
                {episode.description && <div><span className="text-sm text-muted-foreground">Description</span><p>{episode.description}</p></div>}
                <div className="grid md:grid-cols-3 gap-4 pt-2">
                  <div><span className="text-sm text-muted-foreground">Scheduled</span><p>{episode.scheduledAt ? new Date(episode.scheduledAt).toLocaleDateString() : "—"}</p></div>
                  <div><span className="text-sm text-muted-foreground">Recorded</span><p>{episode.recordedAt ? new Date(episode.recordedAt).toLocaleDateString() : "—"}</p></div>
                  <div><span className="text-sm text-muted-foreground">Published</span><p>{episode.publishedAt ? new Date(episode.publishedAt).toLocaleDateString() : "—"}</p></div>
                </div>
              </div>
            )}
          </div>

          <div className="border rounded-lg p-6">
            <h2 className="font-semibold text-lg mb-4">Content & Notes</h2>
            {isEditing ? (
              <div className="space-y-4">
                <div><label className="block text-sm font-medium mb-1">Topics (comma separated)</label><input type="text" value={Array.isArray(editForm.topics) ? editForm.topics.join(", ") : editForm.topics} onChange={e => setEditForm({ ...editForm, topics: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} className="w-full px-3 py-2 border rounded-md" /></div>
                <div><label className="block text-sm font-medium mb-1">Prep Notes</label><textarea value={editForm.prepNotes || ""} onChange={e => setEditForm({ ...editForm, prepNotes: e.target.value })} className="w-full px-3 py-2 border rounded-md" rows={6} /></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div><span className="text-sm text-muted-foreground block mb-1">Topics</span><div className="flex flex-wrap gap-2">{episode.topics?.length ? episode.topics.map(t => <span key={t} className="px-2 py-1 text-sm bg-secondary rounded-full">{t}</span>) : <span className="text-sm text-muted-foreground">No topics</span>}</div></div>
                <div><span className="text-sm text-muted-foreground block mb-1">Prep Notes</span><div className="prose prose-sm max-w-none whitespace-pre-wrap bg-muted/30 p-3 rounded-md">{episode.prepNotes || "No notes"}</div></div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="border rounded-lg p-6">
            <h3 className="font-medium mb-3">Links</h3>
            {isEditing ? (
              <div className="space-y-3">
                <div><label className="block text-sm font-medium mb-1">Recording URL</label><input type="url" value={editForm.recordingUrl || ""} onChange={e => setEditForm({ ...editForm, recordingUrl: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="Dropbox/Drive link" /></div>
                <div><label className="block text-sm font-medium mb-1">Published URL</label><input type="url" value={editForm.episodeUrl || ""} onChange={e => setEditForm({ ...editForm, episodeUrl: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="Spotify/Apple link" /></div>
              </div>
            ) : (
              <div className="space-y-2">
                {episode.recordingUrl ? <a href={episode.recordingUrl} target="_blank" rel="noopener" className="block text-primary hover:underline truncate">📁 View Recording</a> : <span className="text-sm text-muted-foreground">No recording link</span>}
                {episode.episodeUrl ? <a href={episode.episodeUrl} target="_blank" rel="noopener" className="block text-primary hover:underline truncate">🎧 Listen to Episode</a> : <span className="text-sm text-muted-foreground">No public link</span>}
              </div>
            )}
          </div>

          {/* AI Suggested Guests Component */}
          <SuggestedGuests episodeId={episodeId} />

          {/* Email Follow-up Settings */}
          <div className="border rounded-lg p-6">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <span>📧</span> Follow-up Emails
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Automatically send follow-up emails to {episode.guest.name} after this episode goes live.
            </p>
            <Link
              to="/settings/email-followups"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              Configure email settings →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
